import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
//helpers
import { convertDateToDDMMYYYY } from 'helpers';

// Función helper para formatear números con coma como separador decimal y 2 decimales
const formatNumber = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  return parseFloat(value).toFixed(2).replace('.', ',');
};

// Función helper para obtener el total y IVA de un artículo
const getArticleTotals = (article) => {
  // Calcular IVA como vat_amount × quantity
  const vatAmount = parseFloat(article?.vat_amount) || 0;
  const quantity = parseInt(article?.quantity) || 1;
  const vat = vatAmount * quantity;
  
  // Calcular IMPORTE como (precio_venta × quantity) - IVA
  let precioVenta = parseFloat(article?.precio_venta) || 0;
  
  const descuento = parseFloat(article?.descuento) || 0;
  if (descuento > 0.00) {
    precioVenta = precioVenta * (1 - descuento / 100);
  }
  
  const total = (precioVenta * quantity) - vat;
  
  return {
    total: total,
    vat: vat
  };
};

// EXCEL
export const exportToExcel = async (t, data, type) => {
  // Carga diferida dinamicamente porque este paquete no es un componente de React por lo que el lazy no funciona con el
  const ExcelJS = (await import('exceljs')).default;

  const invoice = t('facturas').split('s')[0];
  const order = t('order');
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(type === 'invoice' ? invoice : order);

  // Estilos
  const headerLabelStyle = {
    font: { bold: true },
    alignment: { horizontal: 'left' },
    fill: {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'D3D3D3' } // Color gris claro para el fondo
    }
  };

  const labelsStyle = {
    font: { bold: true },
    alignment: { horizontal: 'left' },
  };

  const valueStyle = {
    alignment: { horizontal: 'left' }
  };

  const lastTwoStyle = {
    alignment: { horizontal: 'right' }
  };

  const subTotalsStyle = {
    font: { bold: true },
    alignment: { horizontal: 'right' }
  };

  let invoiceData = [];

  if (type === 'invoice') {
    worksheet.addRow([`${invoice} de ${data.company?.fiscal_name || '-'} - CIF: ${data?.company?.data?.cif || data?.empresa?.data?.cif || '-'}`])
      .eachCell({ includeEmpty: true }, (cell) => cell.style = labelsStyle);
    // Crear encabezados y datos de la factura
    invoiceData = [
      [`${t('date')}:`, new Date(data?.updated).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) || '-'],
      [`${invoice}:`, '#' + data.identifier || '-'],
      [`${t('librerias').split('s')[0]}:`, data.order?.benefited_company?.name || '-'],
      [`${t('name')}:`, data.order?.user?.full_name || '-'],
      [`${t('document')}:`, data.order?.user?.dni || '-'],
      [`${t('direccion')}:`, data.order?.billing_address?.address || data.billing_address?.address || '-'],
      [`${t('pais')}:`,data.order?.billing_address?.country?.label || data.billing_address?.country?.label || '-']
    ];
  } else {
    worksheet.addRow([`${order} de ${data.empresa?.fiscal_name || data.empresa?.name || '-'} - CIF: ${data.empresa?.data?.cif || data.empresa?.cif || '-'}`])
      .eachCell({ includeEmpty: true }, (cell) => cell.style = labelsStyle);
    // Crear encabezados y datos de los pedidos
    invoiceData = [
      [`${t('date')}:`, new Date(data.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) || '-'],
      [`${order}:`, '#' + data.code || '-'],
      [`${t('librerias').split('s')[0]}:`, data?.benefited_company?.name || data.biblioteca?.denominacion || '-'],
      [`${t('name')}:`, data?.user?.full_name || '-'],
      [`${t('document')}:`, data?.user?.dni || '-'],
      [`${t('direccion')}:`, data.billing_address?.address || '-'],
      [`${t('pais')}:`, data.billing_address?.country?.label || '-']
    ];
  }

  invoiceData.forEach(row => {
    worksheet.addRow(row)
      .eachCell({ includeEmpty: true }, (cell, colNumber) => {
        if (colNumber === 1) cell.style = labelsStyle; // Etiquetas
        if (colNumber === 2) cell.style = valueStyle; // Valores
      });
  });

  // Agregar una fila vacía para separación
  worksheet.addRow();
  // Agregar encabezados de tabla
  worksheet.addRow(['CTD', 'ISBN/CÓDIGO', 'DESCRIPCIÓN PRODUCTO', 'IMPORTE (EUR)', 'IVA (EUR)'])
    .eachCell({ includeEmpty: true }, (cell) => cell.style = headerLabelStyle);

  // Agregar filas de datos - Los artículos pueden estar en data.order.articles o data.articles
  const articles = data.order?.articles || data.articles || [];
  articles.forEach(article => {
    const { total, vat } = getArticleTotals(article);
    worksheet.addRow([
      article?.quantity || 1,
      article?.product?.ean || '',
      article?.product?.title || '',
      formatNumber(total),
      formatNumber(vat)
    ]).eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (colNumber === 4 || colNumber === 5)
        cell.style = lastTwoStyle; // Alinea a la derecha las columnas de valores
      else
        cell.style = valueStyle; // Alinea a la izquierda las columnas de valores
    });
  });

  // Agregar una fila vacía para separación
  worksheet.addRow();

  // Calcular posiciones para subtotales - Los artículos pueden estar en data.order.articles o data.articles
  const subtotalTotal = articles.reduce((acc, article) => {
    const { total } = getArticleTotals(article);
    return acc + total;
  }, 0);
  const subtotalVat = articles.reduce((acc, article) => {
    const { vat } = getArticleTotals(article);
    return acc + vat;
  }, 0);
  
  // Calcular coste de envío
  // Intentar obtenerlo directamente del objeto data, si no existe, calcularlo como diferencia
  let shippingCost = 0;
  if (data?.shipping_cost !== undefined) {
    shippingCost = parseFloat(data.shipping_cost) || 0;
  } else if (data?.order?.shipping_cost !== undefined) {
    shippingCost = parseFloat(data.order.shipping_cost) || 0;
  } else if (data?.total_order !== undefined) {
    // Calcular como diferencia entre total_order y (subtotal + IVA)
    const totalOrder = parseFloat(data.total_order) || 0;
    const subtotalWithVat = subtotalTotal + subtotalVat;
    shippingCost = Math.max(0, totalOrder - subtotalWithVat);
  }
  
  const totalGeneral = subtotalTotal + subtotalVat;
  
  // Agregar subtotales alineados a la derecha
  worksheet.addRow([null, null, null, 'Subtotal Importe (EUR):',
  formatNumber(subtotalTotal)])
    ?.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (colNumber === 4) cell.style = headerLabelStyle; // Alinea a la izquierda el label y en negrita
      if (colNumber === 5) cell.style = subTotalsStyle; // Alinea a la derecha el subtotal
    });
  worksheet.addRow([null, null, null, 'Subtotal IVA (EUR):', formatNumber(subtotalVat)])
    ?.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (colNumber === 4) cell.style = headerLabelStyle; // Alinea a la izquierda el label y en negrita
      if (colNumber === 5) cell.style = subTotalsStyle; // Alinea a la derecha el subtotal
    });
  worksheet.addRow([null, null, null, 'Total General (EUR):', formatNumber(totalGeneral)])
    .eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (colNumber === 4) cell.style = headerLabelStyle; // Alinea a la izquierda el label y en negrita
      if (colNumber === 5) cell.style = subTotalsStyle; // Alinea a la derecha el subtotal
    });

  // Ajustar el ancho de las columnas
  worksheet.columns?.forEach(column => {
    column.width = 20; // Ajustar ancho según sea necesario
  });

  // Crear y devolver el buffer como un Blob
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

// PDF
export const exportToPDFInvoice = async (t, data) => {
  // Carga diferida dinamicamente porque estos paquetes no son componentes de React por lo que el lazy no funciona con ellos
  const { jsPDF } = await import("jspdf");
  const autoTable = (await import('jspdf-autotable')).default;

  const invoice = t('facturas').split('s')[0];
  // Crear documento PDF
  const doc = new jsPDF();

  // Calcular el ancho de la pagina
  const pageWidth = doc.internal.pageSize.getWidth();

  const xPosition = 10;
  let tablePosition = 20;

  // Función para cargar la imagen de forma asíncrona
  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.src = url;
      image.onload = () => resolve(image);
      image.onerror = (error) => reject(error);
    });
  };

  // Ajustar estilos
  doc.setFont('helvetica');
  doc.setFontSize(12);

  // Obtener la fecha actual en formato dd/mm/yyyy
  const currentDate = new Date(data.updated).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  // Definir textos
  const titleText = `${invoice} de ${data?.company?.fiscal_name || '-'} - CIF: ${data?.company?.data?.cif || '-'}`; 
  const dateText = `${t('date')}: ${currentDate || '-'}`;
  const invoiceText = `${invoice}: #${data.identifier || '-'}`;
  const bookStoteText = `${t('librerias').split('s')[0]}: ${data?.order?.benefited_company?.name || '-'}`;
  const userFullNameText = `${data?.order?.user?.full_name || '-'}`;
  const userDocumentText = `${t('document')}: ${data?.order?.user?.dni || '-'}`;
  const userAddressText = `${data?.order?.billing_address?.address || data?.billing_address?.address || '-'}`;
  const countryText = `${t('pais')}: ${data?.order?.billing_address?.country?.label || data?.billing_address?.country?.label || '-'}`;

  // Calcular las posiciones
  const dateTextWidth = doc.getTextWidth(dateText);
  const dateInvoiceXPosition = pageWidth - dateTextWidth - 10;

  const imageUrl = data.company?.logo || '';
  try {
    if (imageUrl) {
      const image = await loadImage(imageUrl);
      const maxImageHeight = 20; // Altura máxima de la imagen
      let imageWidth = 50; // Valor inicial de ancho, se ajustará según el alto
      let imageHeight = image.height / image.width * imageWidth;

      // Asegurarse de que la imagen no supere la altura máxima de 20px
      if (imageHeight > maxImageHeight) {
        imageHeight = maxImageHeight;
        imageWidth = image.width / image.height * imageHeight; // Ajustar el ancho para mantener la proporción
      }

      const imageX = 10; // Posición x para la imagen
      const imageY = 10; // Posición y para la imagen
      // Obtener la parte después del último punto en la URL
      const lastDotIndex = imageUrl.lastIndexOf('.');

      if (lastDotIndex === -1) {
        // No hay punto en la URL, por lo que no hay extensión
        return null;
      }

      // Obtener la extensión del archivo
      const extension = imageUrl.substring(lastDotIndex + 1).split(/[?#]/)[0];

      // Añadir la imagen al PDF
      doc.addImage(imageUrl, extension.toUpperCase(), imageX, imageY, imageWidth, imageHeight);

      // Ajustar la posición de inicio de la tabla
      tablePosition = imageY + imageHeight + 50;

      // Añadir textos (con imagen)
      doc.text(titleText, xPosition, tablePosition - 10);
      doc.text([dateText, invoiceText], dateInvoiceXPosition, tablePosition - 10, { lineHeightFactor: 1.30 });
      doc.text(bookStoteText, xPosition, tablePosition + 2);
      doc.line(xPosition, tablePosition + 4, pageWidth - xPosition, tablePosition + 4);

      // Ajustar estilos
      doc.setFont('helvetica');
      doc.setFontSize(10);
      doc.text([userFullNameText, userAddressText], xPosition, tablePosition + 30, { lineHeightFactor: 1.30 });
      doc.text([userDocumentText, countryText], pageWidth - doc.getTextWidth(userDocumentText) - 10, tablePosition + 10, { lineHeightFactor: 1.30 });
    } else {
      // Añadir textos (sin imagen)
      doc.text(titleText, xPosition, 16);
      doc.text([dateText, invoiceText], dateInvoiceXPosition, 16, { lineHeightFactor: 2 });
      doc.text(bookStoteText, xPosition, 34);
      doc.line(xPosition, 36, pageWidth - xPosition, 36);

      // Ajustar estilos
      doc.setFont('helvetica');
      doc.setFontSize(10);
      doc.text([userFullNameText, userAddressText], xPosition, 42, { lineHeightFactor: 1.30 });
      doc.text([userDocumentText, countryText], pageWidth - doc.getTextWidth(userDocumentText) - 10, 42, { lineHeightFactor: 1.30 });
      doc.line(10, tablePosition + 30, pageWidth - 10, tablePosition + 30); 
    }

    // Obtener los datos de la tabla - Los artículos pueden estar en data.order.articles o data.articles
    const articles = Array.isArray(data.order?.articles) ? data.order.articles : (Array.isArray(data.articles) ? data.articles : []);
    const preparedData = articles?.map((article) => {
      const { total, vat } = getArticleTotals(article);
      return [
        article?.quantity || 1,
        article?.product?.ean || '',
        article?.product?.title || '',
        formatNumber(total),
        formatNumber(vat)
      ];
    });

    // Calcular los subtotales
    const subtotalTotal = articles?.reduce((acc, article) => {
      const { total } = getArticleTotals(article);
      return acc + total;
    }, 0);
    const subtotalVat = articles?.reduce((acc, article) => {
      const { vat } = getArticleTotals(article);
      return acc + vat;
    }, 0);
    
    // Calcular coste de envío
    // Intentar obtenerlo directamente del objeto data, si no existe, calcularlo como diferencia
    let shippingCost = 0;
    if (data?.shipping_cost !== undefined) {
      shippingCost = parseFloat(data.shipping_cost) || 0;
    } else if (data?.order?.shipping_cost !== undefined) {
      shippingCost = parseFloat(data.order.shipping_cost) || 0;
    } else if (data?.total_order !== undefined) {
      // Calcular como diferencia entre total_order y (subtotal + IVA)
      const totalOrder = parseFloat(data.total_order) || 0;
      const subtotalWithVat = subtotalTotal + subtotalVat;
      shippingCost = Math.max(0, totalOrder - subtotalWithVat);
    }
    
    const totalGeneral = parseFloat(subtotalTotal) + parseFloat(subtotalVat);
    // Configuración de la tabla
    autoTable(doc, {
      head: [['CTD', 'ISBN/CÓDIGO', 'DESCRIPCIÓN PRODUCTO', 'IMPORTE (EUR)', 'IVA (EUR)']],
      body: [...preparedData, []],
      startY: tablePosition + 30, // Posición ajustada después de la imagen y el texto
      margin: { top: 10, right: 10, bottom: 10, left: 10 },
      styles: {
        lineWidth: 0,
        halign: 'left',
        cellPadding: 3, // Agrega espacio interno en las celdas
      },
      headStyles: {
        fillColor: false,
        textColor: [0, 0, 0],
        lineWidth: 0,
        halign: 'left',
      },
      bodyStyles: {
        fillColor: false,
        textColor: [0, 0, 0],
        lineWidth: 0,
        halign: 'left',
      },
      didDrawCell: function (data) {
        if (data.row.section === 'head') {
          const startX = 10;
          const endX = doc.internal.pageSize.getWidth() - 10;
          const yPosition = data.cell.y + data.cell.height;
          doc.setDrawColor(44, 62, 80);
          doc.setLineWidth(0.75);
          doc.line(startX, yPosition, endX, yPosition);
        }
      },
      didDrawPage: function (data) {
        const startX = 10;
        const endX = doc.internal.pageSize.getWidth() - 10;
        const yPosition = data.cursor.y;
        doc.line(startX, yPosition, endX, yPosition);
      }
    });

    // Añadir subtotales y total general
    autoTable(doc, {
      startY: doc.autoTable.previous.finalY + 10,
      body: [
        ['Subtotal Importe (EUR):', formatNumber(subtotalTotal)],
        ['Subtotal IVA (EUR):', formatNumber(subtotalVat)],
        ['Total General (EUR):', formatNumber(totalGeneral)]
      ],
      headStyles: {
        fillColor: false,
        textColor: [0, 0, 0],
        fontStyle: 'bold',
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
      },
      styles: {
        fontSize: 12,
        halign: 'right',
      },
      margin: { left: 10, right: 10 },
      theme: 'striped',
    });

    return doc.output('blob');
  } catch (error) {
    console.error('Error loading image:', error);
    toast.error(`${t('errorLoadImage')}: ${error.message}`, { theme: 'colored' });
  }
};

export const createFilesToExport = async (t, selectedData, ext, typeExcel) => {
  const failedInvoices = [];
  try {
    if (selectedData.length > 1) {
      const zip = new JSZip();
      // Crear una lista de promesas para cada archivo PDF
      const promises = selectedData.map(async (inv) => {
        const docBlob = ext === 'pdf' ? await exportToPDFInvoice(t, inv) : await exportToExcel(t, inv, typeExcel); // Espera el PDF Blob
        if (docBlob)
          zip.file(ext === 'pdf' ? `${inv.identifier}.pdf` : `${typeExcel === 'invoice' ? inv.identifier : inv.code}.xlsx`, docBlob);
        else
          failedInvoices.push(typeExcel === 'invoice' ? inv.identifier : inv.code)
      });

      // Esperar que todas las promesas se resuelvan
      await Promise.all(promises);

      // Generar el ZIP y descargarlo
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `${typeExcel === 'invoice' ? 'invoices' : 'orders'}_${convertDateToDDMMYYYY(new Date())}.zip`);
    } else {
      const title = `${typeExcel === 'invoice' ? 'invoice' : 'order'}_${convertDateToDDMMYYYY(new Date())}_${typeExcel === 'invoice' ? selectedData[0].identifier : selectedData[0].code}.`
      const file = ext === 'pdf' ? title + 'pdf' : title + 'xlsx'
      const docBlob = ext === 'pdf' ? await exportToPDFInvoice(t, selectedData[0]) : await exportToExcel(t, selectedData[0], typeExcel); // Espera el PDF Blob
      if (docBlob)
        saveAs(docBlob, file);
      else
        failedInvoices.push(typeExcel === 'invoice' ? selectedData[0].identifier : selectedData[0].code)
    }
  } catch (error) {
    console.error(error); // Este mensaje se puede eliminar en producción
    toast.error(t('errorExportPDFEXCEL'), { theme: 'colored' });
  } finally {
    if (failedInvoices.length > 0) {
      const multiple = failedInvoices.length === 1;
      setTimeout(() => {
        if (!multiple)
          toast.error(`${typeExcel === 'invoice' ? t('multipleInvoices') : t('multipleOrders')} ${failedInvoices.join(', ')} ${t('multipleInvoicesExport')}`,
            { theme: 'colored' });
        else
          toast.error(`${typeExcel === 'invoice' ? t('singleInvoice') : t('singleOrder')} ${failedInvoices.join(', ')} ${t('singleInvoiceExport')}`,
            { theme: 'colored' });
      }, selectedData.length > 1 ? 3000 : 1000);
    }
  }
};